// @flow
import ChartNodes from 'components/OrgNav/OrgChart/ChartNodes'

function gatherAllRoles(node: Object, parent: ?Object = null): Array<Object> {
  const {children, ...nodeWithoutChildren} = node
  return [
    {parent, ...nodeWithoutChildren},
    ...(children || []).flatMap((child) => gatherAllRoles(child, nodeWithoutChildren)),
  ]
}

function roleId(id: string): string {
  id = String(id)
  return id.includes(',') ? id.split(',')[0] : id
}
/**
 * A singleton cache for roles data that can be shared across components
 * This significantly improves performance by processing org chart data only once
 * and making it available to multiple consumers like CKEditor and role dropdowns
 */
class RoleCache {
  static instances: {[string]: Object} = {}

  static instance(orgId: string): RoleCache {
    if (!this.instances[orgId])
      this.instances[orgId] = new RoleCache(orgId)

    return this.instances[orgId]
  }

  orgId: string
  allRoles: ?Array<Object>

  constructor(orgId: string) {
    this.orgId = orgId
    this.allRoles = null
    this.initialize()
  }

  initialize() {
    const chartNodes = ChartNodes.instance(this.orgId)
    if (!chartNodes)
      throw new Error('ChartNodes instance not found')

    // Use current data synchronously if available
    const currentData = chartNodes.currentChartData()
    if (currentData)
      this.allRoles = gatherAllRoles(currentData)
  }

  /**
   * Get all roles from the cache
   * @return {Array<Object>} All roles in the organization
   */
  getAllRoles(): Array<Object> {
    return (this.allRoles || []).filter((role) => role.parent !== null)
  }

  /**
   * Get roles by their IDs
   * @param {Array<string>} roleIds - Array of role IDs to filter by
   * @return {Array<Object>} Filtered roles
   */
  getRolesById(roleIds: Array<string>): Array<Object> {
    const normalizedRoleIds = roleIds.map((id) => roleId(String(id)))
    return this.getAllRoles().filter((role) => normalizedRoleIds.includes(roleId(String(role.id))))
  }

  /**
   * Get sub-roles of a parent role/circle
   * @param {string|number} parentRoleId - The ID of the parent role/circle
   * @return {Array<Object>} Direct child roles of the parent
   */
  getSubRoles(parentRoleId: string): Array<Object> {
    if (!this.allRoles)
      return []

    // Find the parent role
    const parentRole = this.allRoles.find((role) => roleId(role.id) === roleId(String(parentRoleId)))

    if (!parentRole)
      return []

    // Return roles where the parent matches the parentRole
    return (this.allRoles || []).filter(
      (role) => role.parent && roleId(role.parent.id) === roleId(String(parentRoleId)),
    )
  }
}

export default RoleCache
